import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { NextButton } from 'partner360/components/QBR/components/qbrDocumentDetails/components/nextButton/NextButton'
import {
  collapseSobAchievementTableRow,
  expandSobAchievementTableRow,
  selectQBRDocumentFormData,
  selectQBRSOBAchievement,
} from 'partner360/features/QBR'
import { selectQbrSelectedPeriod } from 'partner360/features/QBR/selectors/selectQbrSelectedPeriod'
import { IQBRSobAchievement } from 'partner360/models/qbr/IQBRSobAchievement'

import { FinancialDataVisualizer } from '../../../../../shared/financialDataVisualizer/FinancialDataVisualizer'
import { TRANSLATION_KEYS } from '../../../../../translations'
import { useQbrSave } from '../../../hooks/useQbrSave'
import { QBRErrorModal } from '../settingsStep/components/qbrErrorModal/QBRErrorModal'

import { getSobAchievementColumns } from './helpers/getSobAchievementColumns'
import { getSobAchievementShortedColumns } from './helpers/getSobAchievementShortedColumns'

import styles from './SobAchievement.module.scss'

export const SobAchievement = () => {
  const data = useSelector(selectQBRSOBAchievement)
  const selectedPeriod = useSelector(selectQbrSelectedPeriod)
  const document = useSelector(selectQBRDocumentFormData)

  const { isSavingProgress, onNextButtonClick, showErrorModal, onCloseErrorModal } = useQbrSave()
  const t = useSafetyTranslation()
  const dispatch = useAppDispatch()

  const showUpToDateColumn = useMemo(() => {
    if (!document) {
      return false
    }

    return document.DocumentHideUtD === 0
  }, [document])

  const selectedPeriodFYear = useMemo(() => selectedPeriod.fYear, [selectedPeriod.fYear])
  const selectedPeriodFQuarters = useMemo(
    () => selectedPeriod.fQuarters.map(({ fQuarter }) => fQuarter),
    [selectedPeriod.fQuarters]
  )

  const columns = useMemo(
    () => getSobAchievementColumns({ showUpToDateColumn, selectedPeriodFQuarters }),
    [selectedPeriodFQuarters, showUpToDateColumn]
  )

  const columnsForCharts = useMemo(
    () =>
      getSobAchievementShortedColumns({
        currentFYear: selectedPeriodFYear,
        selectedPeriodFQuarters,
      }),
    [selectedPeriodFYear, selectedPeriodFQuarters]
  )

  const expandedNodes = useMemo(() => {
    return data.filter((el) => el.IsExpanded)
  }, [data])

  const getBarsSettings = useCallback(
    (item: IQBRSobAchievement) => {
      return [
        {
          name: t(TRANSLATION_KEYS.financialTable.yoyPlan),
          color: '#4858F5',
          data: [
            item.PrevYearQ1Plans,
            item.PrevYearQ2Plans,
            item.PrevYearQ3Plans,
            item.PrevYearQ4Plans,
            item.CurrentYearQ1Plans,
            item.CurrentYearQ2Plans,
            item.CurrentYearQ3Plans,
            item.CurrentYearQ4Plans,
          ],
        },
        {
          name: t(TRANSLATION_KEYS.financialTable.yoyActual),
          color: '#00D072',
          data: [
            item.PrevYearQ1Actuals,
            item.PrevYearQ2Actuals,
            item.PrevYearQ3Actuals,
            item.PrevYearQ4Actuals,
            item.CurrentYearQ1Actuals,
            item.CurrentYearQ2Actuals,
            item.CurrentYearQ3Actuals,
            item.CurrentYearQ4Actuals,
          ],
        },
      ]
    },
    [t]
  )

  const getLinesSettings = useCallback(
    (item: IQBRSobAchievement) => {
      return [
        {
          name: t(TRANSLATION_KEYS.financialTable.yoySob),
          color: '#FF63BB',
          data: [
            item.PrevYearQ1YoYActuals,
            item.PrevYearQ2YoYActuals,
            item.PrevYearQ3YoYActuals,
            item.PrevYearQ4YoYActuals,
            item.CurrentYearQ1YoYActuals,
            item.CurrentYearQ2YoYActuals,
            item.CurrentYearQ3YoYActuals,
            item.CurrentYearQ4YoYActuals,
          ],
        },
      ]
    },
    [t]
  )

  const onExpand = useCallback(
    (keyField: string, keyFieldValue: string | number, hierarchyField: string) => {
      dispatch(expandSobAchievementTableRow({ keyField, keyFieldValue, hierarchyField }))
    },
    [dispatch]
  )

  const onCollapse = useCallback(
    (keyField: string, keyFieldValue: string | number, hierarchyField: string) => {
      dispatch(collapseSobAchievementTableRow({ keyField, keyFieldValue, hierarchyField }))
    },
    [dispatch]
  )

  return (
    <div>
      <FinancialDataVisualizer
        className={styles.table}
        currencyLabel={t(TRANSLATION_KEYS.financialTable.chartLabelSob)}
        chartLabelNode={<Translation text={TRANSLATION_KEYS.financialTable.chartLabelSob} />}
        data={data}
        columns={columns}
        chartNameField={'Name'}
        getBarsSettings={getBarsSettings}
        expandedNodes={expandedNodes}
        onCollapse={onCollapse}
        onExpand={onExpand}
        columnsForCharts={columnsForCharts}
        getLinesSettings={getLinesSettings}
        showUpToDateColumn={showUpToDateColumn}
        selectedPeriodFQuarters={selectedPeriodFQuarters}
        selectedPeriodFYear={selectedPeriod.fYear}
        isSob
      />
      <NextButton onClick={onNextButtonClick} />
      <QBRErrorModal
        show={showErrorModal}
        onCloseButtonClick={onCloseErrorModal}
        isButtonDisabled={isSavingProgress}
      />
    </div>
  )
}
